import React from 'react';
import { Modal, Button } from 'antd';
import { useHistory } from 'react-router-dom';
import qs from 'qs';
import { useWhiteLabellingContext } from 'contexts/WhiteLabellingContext';
import { useAuthGateChooseAccount } from 'hooks/data/user/useAuthGate';
import { UserType } from 'further-types/user';
import { AUTH_GATE_CHOOSE_ACCOUNT } from 'routes/CommonRoutes';

interface SwitchAccountModalProps {
  accountId: string;
  userType: UserType;
  isOpen: boolean;
  onClose: () => void;
}

const accountTypeLabel = {
  [UserType.Investor]: 'Investor',
  [UserType.Adviser]: 'Adviser',
  [UserType.Organisation]: 'Organisation',
};

const SwitchAccountModal: React.FC<SwitchAccountModalProps> = ({
  accountId,
  userType,
  isOpen,
  onClose,
}) => {
  const history = useHistory();
  const { linkedFirmIds, updatePendingAccount } = useWhiteLabellingContext();
  const { accountsList } = useAuthGateChooseAccount({
    firmIds: linkedFirmIds,
  });

  const handleCancel = () => {
    onClose();
    history.push('/');
  };

  const handleSwitch = () => {
    updatePendingAccount({ accountId, userType });

    // Strip the relevant params themselves to prevent infinite loop
    const parsedSearch = qs.parse(history.location.search.slice(1));
    delete parsedSearch.accountId;
    delete parsedSearch.userType;
    const cleanedSearch = qs.stringify(parsedSearch);

    history.push(AUTH_GATE_CHOOSE_ACCOUNT, {
      from: {
        pathname: history.location.pathname,
        search: cleanedSearch ? `?${cleanedSearch}` : '',
        hash: history.location.hash,
      },
    });
  };

  if (accountsList.isLoading || !accountsList.data) {
    return null;
  }

  const investor = accountsList.data.investors?.find(
    (inv) => inv._id === accountId,
  );

  let userAccount: {
    data: { _id: string; fullName: string };
    userType: UserType;
  } | null = null;

  switch (userType) {
    case UserType.Investor:
      if (investor)
        userAccount = { data: investor, userType: UserType.Investor };
      break;
    case UserType.Adviser:
      if (accountsList.data.adviser?._id === accountId) {
        userAccount = {
          data: accountsList.data.adviser,
          userType: UserType.Adviser,
        };
      }
      break;
    case UserType.Organisation:
      if (accountsList.data.orgUser?._id === accountId) {
        userAccount = {
          data: accountsList.data.orgUser,
          userType: UserType.Organisation,
        };
      }
      break;
  }

  if (!userAccount) {
    history.push('/');
  }

  const hasMultipleAccountTypes =
    [
      accountsList.data?.investors && accountsList.data.investors.length > 0,
      !!accountsList.data?.adviser,
      !!accountsList.data?.orgUser,
    ].filter(Boolean).length > 1;

  return (
    <Modal
      title="Change your account"
      open={isOpen}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="switch" type="primary" onClick={handleSwitch}>
          Switch account
        </Button>,
      ]}
    >
      <p>
        The link you've clicked is for{' '}
        <strong>
          {userAccount?.data.fullName}
          {hasMultipleAccountTypes && userAccount?.userType !== undefined && (
            <> ({accountTypeLabel[userAccount?.userType]})</>
          )}
        </strong>
        . To continue, switch your account by clicking below.
      </p>
    </Modal>
  );
};

export default SwitchAccountModal;
